import { useMemo, useState } from "react";
import { Can, useCan } from "@/auth/permissions";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { vehicleOpsApi, type FleetLicense, type VehicleExpense } from "@/services/vehicleOps";
import { listVehicles } from "@/services/vehicles";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { Card } from "@/shared/ui/Card";
import { VehicleOpsTabs } from "./VehicleOpsTabs";

const usd = (c: number) => "$" + (c / 100).toLocaleString("en-US");

const EXP_CAT: Record<string, { label: string; cls: string }> = {
  vehicle_maintenance: { label: "🔧 维修", cls: "bg-blue-50 text-blue-700" },
  fuel: { label: "⛽ 油费", cls: "bg-emerald-50 text-emerald-700" },
  insurance: { label: "🛡 保险", cls: "bg-purple-50 text-purple-700" },
  fine: { label: "💳 罚款", cls: "bg-rose-50 text-rose-700" },
  parking: { label: "🅿️ 停车", cls: "bg-slate-100 text-slate-600" },
  other: { label: "❓ 其他", cls: "bg-slate-100 text-slate-600" },
};
const EXP_TABS = [
  { key: "all", label: "全部" },
  { key: "vehicle_maintenance", label: "🔧 维修" },
  { key: "fuel", label: "⛽ 油费" },
  { key: "insurance", label: "🛡 保险" },
  { key: "fine", label: "💳 罚款" },
];
const LIC_TYPE: Record<string, string> = {
  cua: "🚌 CUA 商业用户授权", dot_authorization: "🇺🇸 DOT 联邦运输证", commercial_insurance: "🛡 商业保险",
  coi: "🛡 COI 保险证书", annual_inspection: "🔍 年检", registration: "📋 车辆注册", other: "➕ 自定义",
};
const LIC_WARN: Record<string, { row: string; badge: string; label: string }> = {
  green: { row: "border-slate-200 bg-white", badge: "bg-emerald-100 text-emerald-700", label: "✅ 正常" },
  yellow: { row: "border-amber-200 bg-amber-50", badge: "bg-amber-100 text-amber-700", label: "🟡 60 天内" },
  red: { row: "border-rose-200 bg-rose-50", badge: "bg-rose-100 text-rose-700", label: "🔴 30 天内" },
  expired: { row: "border-rose-300 bg-rose-100", badge: "bg-rose-200 text-rose-900", label: "⚫ 已过期" },
};

const EXP_STATUS: Record<string, { label: string; cls: string }> = {
  pending: { label: "待审", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "已批", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "已拒", cls: "bg-rose-100 text-rose-700" },
};

export function VehicleOpsExpenses() {
  const canApproveExpense = useCan("vehicle_ops.expenses.approve");
  const qc = useQueryClient();
  const [expTab, setExpTab] = useState("all");
  const [licVeh, setLicVeh] = useState("");
  const [sel, setSel] = useState<VehicleExpense | null>(null);

  const expQ = useQuery({ queryKey: ["vehicle-ops", "expenses"], queryFn: () => vehicleOpsApi.listExpenses() });
  const licQ = useQuery({ queryKey: ["vehicle-ops", "licenses"], queryFn: () => vehicleOpsApi.listFleetLicenses() });
  const vehQ = useQuery({ queryKey: ["vehicles", "all"], queryFn: () => listVehicles() });
  const maintQ = useQuery({ queryKey: ["vehicle-ops", "maintenance"], queryFn: () => vehicleOpsApi.listMaintenance() });

  const maintMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of maintQ.data ?? []) m.set(o.id, o.order_number);
    return m;
  }, [maintQ.data]);

  const vehMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of vehQ.data ?? []) m.set(v.id, v.vehicle_number);
    return m;
  }, [vehQ.data]);
  const vehName = (id: string | null) => (id ? vehMap.get(id) ?? "—" : "—");

  const approve = useMutation({
    mutationFn: (id: string) => vehicleOpsApi.approveExpense(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vehicle-ops"] }); setSel(null); },
    onError: (e) => alert((e as Error).message || "审批失败"),
  });
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => vehicleOpsApi.rejectExpense(id, reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vehicle-ops"] }); setSel(null); },
    onError: (e) => alert((e as Error).message || "驳回失败"),
  });
  function doReject(id: string) {
    const r = window.prompt("驳回理由：");
    if (r) reject.mutate({ id, reason: r });
  }

  if (expQ.isLoading || licQ.isLoading) return <LoadingState />;
  if (expQ.isError || licQ.isError) return (
    <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500">
      <span>费用/证照加载失败</span>
      <button onClick={() => { expQ.refetch(); licQ.refetch(); }} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
    </div>
  );
  const expenses = (expQ.data ?? []).filter((e) => expTab === "all" || e.category === expTab);
  const licenses = (licQ.data ?? []).filter((l) => !licVeh || l.vehicle_id === licVeh);

  return (
    <PageShell title="💸 费用收据 + 证照管理" subtitle="费用 4 类 + 7 类证照（60/30 预警）· 与财务模块应付账款 mirror">
      <VehicleOpsTabs active="expenses" />

      {/* 费用收据 */}
      <Card
        title="💸 费用收据（与财务应付账款 mirror）"
        action={<Can perm="vehicle_ops.expenses.create"><Link to="/admin/vehicle-operations/expenses/upload" className="rounded-lg bg-blue-600 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-blue-700">+ 上传收据</Link></Can>}
      >
        <div className="flex gap-1">
          {EXP_TABS.map((t) => {
            const n = t.key === "all" ? (expQ.data ?? []).length : (expQ.data ?? []).filter((e) => e.category === t.key).length;
            return (
              <button key={t.key} onClick={() => setExpTab(t.key)} className={`rounded-lg px-3 py-1.5 text-[13px] font-medium ${expTab === t.key ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{t.label} {n}</button>
            );
          })}
        </div>
        <div className="mt-3 overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr><th className="px-3 py-2 text-left">单号</th><th className="px-3 py-2 text-left">车</th><th className="px-3 py-2 text-left">类别</th><th className="px-3 py-2 text-right">金额</th><th className="px-3 py-2 text-right">日期</th><th className="px-3 py-2 text-center">状态</th></tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">暂无费用收据</td></tr>
              ) : expenses.map((e) => (
                <tr key={e.id} onClick={() => setSel(e)} className="cursor-pointer border-t border-slate-100 hover:bg-blue-50">
                  <td className="px-3 py-2 font-medium text-slate-900">{e.receipt_number}</td>
                  <td className="px-3 py-2">{vehName(e.related_vehicle_id)}</td>
                  <td className="px-3 py-2"><span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${EXP_CAT[e.category]?.cls ?? "bg-slate-100 text-slate-600"}`}>{EXP_CAT[e.category]?.label ?? e.category}</span></td>
                  <td className="px-3 py-2 text-right font-semibold">{usd(e.amount_cents)}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{e.receipt_date?.slice(5)}</td>
                  <td className="px-3 py-2 text-center"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${EXP_STATUS[e.status]?.cls ?? "bg-slate-100 text-slate-500"}`}>{EXP_STATUS[e.status]?.label ?? e.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[11px] text-slate-400">💡 财务对账 / 推 QB · <span className="font-semibold">待接财务模块</span></div>
      </Card>

      {/* 7 类证照 */}
      <Card
        title="📋 7 类证照管理（每车 · 60/30 双层预警 · 前端实时计算）"
        action={<Can perm="vehicles.licenses.update"><Link to="/admin/vehicle-operations/expenses/license" className="rounded-lg bg-blue-600 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-blue-700">+ 录入证照</Link></Can>}
      >
        <div className="flex items-center gap-2">
          <select value={licVeh} onChange={(e) => setLicVeh(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-[13px]">
            <option value="">全部车</option>
            {(vehQ.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
          </select>
          <span className="text-[11px] text-slate-400">CUA / DOT / 商业保险 / 年检 / 注册 / COI / 自定义 · 平等处理</span>
        </div>
        <div className="mt-3 flex flex-col gap-2">
          {licenses.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-400">暂无证照</div>
          ) : licenses.map((l) => <LicenseRow key={l.id} l={l} veh={vehName(l.vehicle_id)} />)}
        </div>
        <div className="mt-3 rounded-lg bg-emerald-50 p-3 text-[11px] text-emerald-800">
          预警：60 天内 🟡黄 · 30 天内 🔴红 · 已过期 ⚫深红（调度自动 hold 该车）· 实时计算无 cron · 编辑集中此处
        </div>
      </Card>

      {/* 财务对账（mock）*/}
      <Card title="🔗 与财务模块对账状态（只读 mirror · 跳财务编辑）">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-[13px] text-slate-500">
          财务对账 / QB 同步状态 · <span className="font-semibold text-slate-700">待接财务模块</span>（财务模块上线后显示已对账/待确认/本月车辆类总支出）
        </div>
      </Card>

      {/* 费用收据详情 Drawer（点条目展开 + 审批/驳回）*/}
      {sel && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setSel(null)}>
          <div className="h-full w-full max-w-md overflow-y-auto bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
              <h2 className="text-[16px] font-bold text-slate-900">{sel.receipt_number}</h2>
              <button onClick={() => setSel(null)} className="text-2xl leading-none text-slate-400 hover:text-slate-700">×</button>
            </div>
            <div className="flex flex-col gap-4 p-5">
              <div className="flex items-center gap-2">
                <span className={`rounded px-2 py-0.5 text-[12px] font-semibold ${EXP_CAT[sel.category]?.cls ?? "bg-slate-100 text-slate-600"}`}>{EXP_CAT[sel.category]?.label ?? sel.category}</span>
                <span className={`rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${EXP_STATUS[sel.status]?.cls ?? "bg-slate-100"}`}>{EXP_STATUS[sel.status]?.label ?? sel.status}</span>
              </div>
              <DRow label="金额" value={usd(sel.amount_cents)} />
              <DRow label="车辆" value={vehName(sel.related_vehicle_id)} />
              {sel.category === "vehicle_maintenance" && (
                <DRow label="🔧 关联维修工单" value={sel.related_maintenance_order_id ? (maintMap.get(sel.related_maintenance_order_id) ?? "已关联") : "⚠️ 未关联"} />
              )}
              <DRow label="日期" value={sel.receipt_date ?? "—"} />
              <DRow label="说明" value={sel.description ?? "—"} />
              <div className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">🔗 QB 同步状态 · 待接财务模块</div>
              {sel.status === "pending" && canApproveExpense && (
                <div className="flex gap-2 border-t border-slate-200 pt-4">
                  <button onClick={() => doReject(sel.id)} disabled={reject.isPending} className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-[13px] font-medium hover:bg-slate-50">驳回</button>
                  <button onClick={() => approve.mutate(sel.id)} disabled={approve.isPending} className="flex-1 rounded-lg bg-emerald-600 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-emerald-700">批准</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

function DRow({ label, value }: { label: string; value: string }) {
  return <div className="flex flex-col gap-0.5"><span className="text-[11px] font-medium text-slate-500">{label}</span><span className="text-[14px] text-slate-900">{value}</span></div>;
}

function LicenseRow({ l, veh }: { l: FleetLicense; veh: string }) {
  const w = LIC_WARN[l.warning_level] ?? LIC_WARN.green;
  return (
    <div className={`flex items-center justify-between gap-3 rounded-lg border px-3.5 py-2.5 ${w.row}`}>
      <div className="min-w-0">
        <div className="truncate text-[13px] font-semibold text-slate-900">{veh} · {LIC_TYPE[l.license_type] ?? l.license_type}</div>
        <div className="text-[11px] text-slate-500">{l.license_number} · 到期 {l.expires_at}</div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className="text-[13px] font-bold text-slate-900">{l.days_to_expiry < 0 ? `逾期 ${-l.days_to_expiry} 天` : `${l.days_to_expiry} 天`}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${w.badge}`}>{w.label}</span>
      </div>
    </div>
  );
}
